import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import { RefundStatus } from '@jiaofa/constants'
import { FINANCE_TRANSACTION_STATUS, RECONCILIATION_STATUS, fetchFinanceRefunds, fetchFinanceTransactions, fetchReconciliation } from '@/api/finance'

type FormatAmount = (amountInFen: number) => string

export function useFinanceCenter(options: {
  isTransactionsCenter: ComputedRef<boolean>
  isReconciliationCenter: ComputedRef<boolean>
  isRefundTransactionsCenter: ComputedRef<boolean>
  formatAmount: FormatAmount
}) {
  const loadingTransactions = ref(false)
  const loadingReconciliation = ref(false)
  const loadingRefundTransactions = ref(false)
  const transactionPage = ref<Awaited<ReturnType<typeof fetchFinanceTransactions>>>()
  const reconciliationPage = ref<Awaited<ReturnType<typeof fetchReconciliation>>>()
  const refundTransactionPage = ref<Awaited<ReturnType<typeof fetchFinanceRefunds>>>()
  const transactionFilters = reactive({ page: 1, pageSize: 10 })
  const reconciliationFilters = reactive({ page: 1, pageSize: 10 })
  const refundTransactionFilters = reactive({ page: 1, pageSize: 10 })

  const transactionTotalPages = computed(() => !transactionPage.value ? 1 : Math.max(1, Math.ceil(transactionPage.value.total / transactionPage.value.pageSize)))
  const reconciliationTotalPages = computed(() => !reconciliationPage.value ? 1 : Math.max(1, Math.ceil(reconciliationPage.value.total / reconciliationPage.value.pageSize)))
  const refundTransactionTotalPages = computed(() => !refundTransactionPage.value ? 1 : Math.max(1, Math.ceil(refundTransactionPage.value.total / refundTransactionPage.value.pageSize)))

  const transactionStats = computed(() => {
    const list = transactionPage.value?.list ?? []
    const amount = list.reduce((total, item) => total + item.amount, 0)
    return [
      { label: '流水总数', value: String(transactionPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '当前页成功', value: String(list.filter((item) => item.status === FINANCE_TRANSACTION_STATUS.Success).length), sub: '支付成功', tone: 'success' as const },
      { label: '当前页处理中', value: String(list.filter((item) => item.status === FINANCE_TRANSACTION_STATUS.Processing).length), sub: '等待结果', tone: 'warning' as const },
      { label: '当前页金额', value: options.formatAmount(amount), sub: '当前页合计', tone: 'brand' as const },
    ]
  })

  const reconciliationStats = computed(() => {
    const list = reconciliationPage.value?.list ?? []
    const diffAmount = list.reduce((total, item) => total + item.diffAmount, 0)
    return [
      { label: '对账门店', value: String(reconciliationPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '已平账', value: String(list.filter((item) => item.status === RECONCILIATION_STATUS.Balanced).length), sub: '当前页统计', tone: 'success' as const },
      { label: '待确认', value: String(list.filter((item) => item.status === RECONCILIATION_STATUS.Pending).length), sub: '当前页统计', tone: 'warning' as const },
      { label: '差额合计', value: options.formatAmount(diffAmount), sub: '当前页合计', tone: 'error' as const },
    ]
  })

  const refundTransactionStats = computed(() => {
    const list = refundTransactionPage.value?.list ?? []
    const amount = list.reduce((total, item) => total + item.amount, 0)
    return [
      { label: '退款流水总数', value: String(refundTransactionPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '当前页已退款', value: String(list.filter((item) => item.status === RefundStatus.Refunded).length), sub: '退款完成', tone: 'success' as const },
      { label: '当前页处理中', value: String(list.filter((item) => [RefundStatus.Applying, RefundStatus.Reviewing, RefundStatus.Refunding].includes(item.status)).length), sub: '等待处理', tone: 'warning' as const },
      { label: '当前页金额', value: options.formatAmount(amount), sub: '当前页合计', tone: 'error' as const },
    ]
  })

  async function loadTransactions(): Promise<void> {
    if (!options.isTransactionsCenter.value) return
    loadingTransactions.value = true
    try {
      transactionPage.value = await fetchFinanceTransactions({
        page: transactionFilters.page,
        pageSize: transactionFilters.pageSize,
      })
    } catch (error) {
      // 刻意不清空 transactionPage：失败时保留上一次的数据，避免整表闪空。
      // 分页游标（transactionFilters.page）同样保持不变，重试仍打同一页。
      ElMessage.error(error instanceof Error ? error.message : '交易流水加载失败')
    } finally {
      loadingTransactions.value = false
    }
  }

  async function loadReconciliation(): Promise<void> {
    if (!options.isReconciliationCenter.value) return
    loadingReconciliation.value = true
    try {
      reconciliationPage.value = await fetchReconciliation({
        page: reconciliationFilters.page,
        pageSize: reconciliationFilters.pageSize,
      })
    } catch (error) {
      // 同 loadTransactions：失败保留旧数据与旧游标。后端对账是内存分页，
      // 全量聚合耗时随门店数增长，这条路径更容易被触发。
      ElMessage.error(error instanceof Error ? error.message : '门店对账加载失败')
    } finally {
      loadingReconciliation.value = false
    }
  }

  async function loadRefundTransactions(): Promise<void> {
    if (!options.isRefundTransactionsCenter.value) return
    loadingRefundTransactions.value = true
    try {
      refundTransactionPage.value = await fetchFinanceRefunds({
        page: refundTransactionFilters.page,
        pageSize: refundTransactionFilters.pageSize,
      })
    } catch (error) {
      // 同 loadTransactions：失败保留旧数据与旧游标。
      ElMessage.error(error instanceof Error ? error.message : '退款流水加载失败')
    } finally {
      loadingRefundTransactions.value = false
    }
  }

  async function changeTransactionPage(page: number): Promise<void> {
    transactionFilters.page = page
    await loadTransactions()
  }

  async function changeReconciliationPage(page: number): Promise<void> {
    reconciliationFilters.page = page
    await loadReconciliation()
  }

  async function changeRefundTransactionPage(page: number): Promise<void> {
    refundTransactionFilters.page = page
    await loadRefundTransactions()
  }

  return {
    loadingTransactions,
    loadingReconciliation,
    loadingRefundTransactions,
    transactionPage,
    reconciliationPage,
    refundTransactionPage,
    transactionTotalPages,
    reconciliationTotalPages,
    refundTransactionTotalPages,
    transactionStats,
    reconciliationStats,
    refundTransactionStats,
    loadTransactions,
    loadReconciliation,
    loadRefundTransactions,
    changeTransactionPage,
    changeReconciliationPage,
    changeRefundTransactionPage,
  }
}
